import { useMemo, RefObject } from 'react';
import { useAppSelector } from '../../../../store/redux/hooks';

interface FieldRefMap {
  [fieldPattern: string]: RefObject<HTMLDivElement>;
}

interface UseFieldEditIndicatorOptions {
  fieldRefMap?: FieldRefMap;
  prefix?: string;
  index?: number;
  arrayFieldPattern?: string;
  getRefFromIndex?: (index: number) => RefObject<HTMLDivElement> | undefined;
}

interface UseFieldEditIndicatorResult {
  activeFieldRef: RefObject<HTMLDivElement> | null;
  isFieldBeingEdited: boolean;
  activeIndex?: number;
}

export const useFieldEditIndicator = ({
  fieldRefMap,
  prefix,
  index,
  arrayFieldPattern,
  getRefFromIndex,
}: UseFieldEditIndicatorOptions): UseFieldEditIndicatorResult => {
  const focusedField = useAppSelector((s) => s.template.focusedField);

  return useMemo(() => {
    if (!focusedField) {
      return { activeFieldRef: null, isFieldBeingEdited: false };
    }

    if (arrayFieldPattern && getRefFromIndex) {
      if (focusedField.startsWith(arrayFieldPattern)) {
        const fieldIndex = parseInt(focusedField.split('.').pop() || '-1', 10);
        const ref = getRefFromIndex(fieldIndex);
        if (fieldIndex >= 0 && ref) {
          return {
            activeFieldRef: ref,
            isFieldBeingEdited: true,
            activeIndex: fieldIndex,
          };
        }
      }
      return { activeFieldRef: null, isFieldBeingEdited: false };
    }

    if (index !== undefined && prefix && fieldRefMap) {
      for (const [fieldName, ref] of Object.entries(fieldRefMap)) {
        if (fieldName.includes('||')) {
          const patterns = fieldName.split('||').map((p) => p.trim());
          if (
            patterns.some(
              (pattern) => focusedField === `${prefix}.${index}.${pattern}`
            )
          ) {
            return { activeFieldRef: ref, isFieldBeingEdited: true };
          }
        } else {
          const expectedField = `${prefix}.${index}.${fieldName}`;
          if (focusedField === expectedField) {
            return { activeFieldRef: ref, isFieldBeingEdited: true };
          }
        }
      }
      return { activeFieldRef: null, isFieldBeingEdited: false };
    }

    if (prefix && fieldRefMap) {
      for (const [fieldName, ref] of Object.entries(fieldRefMap)) {
        const expectedField = `${prefix}.${fieldName}`;
        if (focusedField === expectedField) {
          return { activeFieldRef: ref, isFieldBeingEdited: true };
        }
      }
      return { activeFieldRef: null, isFieldBeingEdited: false };
    }

    if (fieldRefMap) {
      for (const [fieldName, ref] of Object.entries(fieldRefMap)) {
        if (focusedField === fieldName) {
          return { activeFieldRef: ref, isFieldBeingEdited: true };
        }
      }
    }

    return { activeFieldRef: null, isFieldBeingEdited: false };
  }, [
    focusedField,
    fieldRefMap,
    prefix,
    index,
    arrayFieldPattern,
    getRefFromIndex,
  ]);
};
